import { observer, useLocalObservable } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { HelperText, ScopeTableRow, Table, TableHead, TableHeadCell } from '@ifs-gcp/components';
import type { IScopesNode } from '@ifs-gcp/core';
import type { ProductScopeWidgetChild } from '@ifs-gcp/core/models';

import { useAuditStore } from '@/store/auditStore';
import { useTouchedField } from '@/store/validationStore';
import { getTranslation } from '@/utils/translations';

import { ScopeRow } from './row';

interface WidgetProductScopesProps {
  element: IScopesNode<ProductScopeWidgetChild>;
}

export const WidgetProductScopes = observer(({ element }: WidgetProductScopesProps) => {
  const {
    i18n: { language },
  } = useTranslation();
  const {
    props: { id: widgetId, text, context, scopes },
  } = element;

  const audit = useAuditStore((state) => state.audit)!;

  const { isTouched, markTouched } = useTouchedField(widgetId, context);

  const domain = useLocalObservable(() => audit.factory('productScopes', element));

  const fieldError = isTouched ? audit.status.getErrorByTemplate(element) : undefined;

  const widgetError = fieldError?.errors?.find(({ id: errorId }) => errorId === widgetId);

  if (!scopes) {
    return null;
  }

  return (
    <>
      {widgetError && (
        <HelperText error component="span">
          {getTranslation(widgetError.error, language)}
        </HelperText>
      )}

      <Table
        head={
          <TableHead>
            <TableHeadCell>{getTranslation(text, language)}</TableHeadCell>
          </TableHead>
        }
      >
        {element.props.scopes.map((scope) => (
          <ScopeTableRow key={scope.uuid}>
            <ScopeRow
              domain={domain}
              template={scope}
              errors={fieldError?.errors}
              element={element}
              markTouched={markTouched}
            />
          </ScopeTableRow>
        ))}
      </Table>
    </>
  );
});
